import { EmailApi, emailsApi } from "api/emailsApi";
import { useEmail, useEmails } from "api/hooks/useEmails";
import { useUsersOptions } from "api/hooks/useUsers";
import { AdminResourceDeleteModal } from "components/molecules";
import { ModalHeader } from "components/templates/Modals/ModalHeader";
import { ModalPadding } from "components/templates/Modals/ModalPadding";
import { EMAIL_TEMPLATE_TYPE_OPTIONS } from "consts/emailTemplateType";
import { IDropdownOption, PaziaLoader } from "components/atoms";
import {
  PaziaModal,
  TanTable,
  TanTableColumn,
  useFilters,
  useInitFilters,
  usePagination,
  useSorting,
} from "components/molecules";
import { formatDate } from "utils";
import React, { useState } from "react";
import { MdAccessTime } from "react-icons/md";
import { EmailPageHeader } from "../components/EmailPageHeader";

const FILTER_NAME = "emails-filter";

export const TriggersEmailsPage = () => {
  const { getInitFilterValue, getInitFilterValues } = useInitFilters(FILTER_NAME);
  const { isLoading, options } = useUsersOptions();

  if (isLoading) return <PaziaLoader position="center" />;

  return (
    <EmailsPageInner
      initValues={{
        id: getInitFilterValue("id", "like") ?? "",
        type: getInitFilterValues("type"),
        recipient: getInitFilterValues("recipient"),
      }}
      usersOptions={options}
    />
  );
};

interface EmailsPageInnerProps {
  initValues: {
    id: string;
    type: number[];
    recipient: number[];
  };
  usersOptions: IDropdownOption[];
}
export const EmailsPageInner = ({ usersOptions, initValues }: EmailsPageInnerProps) => {
  const [selectedEmailId, setSelectedEmailId] = useState<number | null>(null);
  const { searchPhrase, filter, updateAllFilters } = useFilters(FILTER_NAME);

  const { limit, onPaginationChange, skip, pagination } = usePagination();
  const { sorting, onSortingChange, fieldWithOrder } = useSorting("senttime", "DESC");
  const { isFetching, isLoading, total, emails, invalidate } = useEmails(
    skip,
    limit,
    searchPhrase,
    filter,
    fieldWithOrder,
  );

  const handleViewEmail = (email: EmailApi) => {
    setSelectedEmailId(email.id);
  };

  const cols: TanTableColumn<EmailApi>[] = [
    {
      id: "id",
      accessorKey: "id",
      header: "ID",
      enableColumnFilter: true,
      enableSorting: true,
      meta: {
        filterVariant: "id",
      },
      size: 20,
      cell: (info) => info.getValue(),
    },
    {
      id: "recipient",
      accessorKey: "recipient",
      header: "Odbiorca",
      size: 150,
      enableColumnFilter: true,
      enableSorting: true,
      meta: {
        filterVariant: "select",
        options: usersOptions,
      },
    },
    {
      id: "type",
      accessorKey: "type",
      header: "Typ",
      size: 200,
      enableColumnFilter: true,
      cell: (info) => EMAIL_TEMPLATE_TYPE_OPTIONS.find((x) => x.value === info.getValue())?.label,
      meta: {
        filterVariant: "select",
        options: EMAIL_TEMPLATE_TYPE_OPTIONS,
      },
    },
    {
      id: "createdDate",
      accessorKey: "createdDate",
      header: "Utworzono",
      cell: (info) => formatDate(info.getValue<Date>(), true, true),
      size: 150,
      enableColumnFilter: false,
    },
    {
      id: "sentTime",
      accessorKey: "sentTime",
      header: "Wysłano",
      cell: (info) => {
        const date = info.getValue<Date>();
        if (date) return formatDate(date, true, true);

        var plannedDeliveryTime = info.row.original.plannedDeliveryTime;
        const plannedDeliveryIs3minutesBeforeNow = plannedDeliveryTime
          ? new Date(plannedDeliveryTime).getTime() - 3 * 60 * 1000 < new Date().getTime()
          : false;
        const plannedText = plannedDeliveryIs3minutesBeforeNow ? "Opóźnione" : "Planowana wysyłka";
        if (plannedDeliveryTime)
          return (
            <span
              className={`flex items-center gap-2 ${
                plannedDeliveryIs3minutesBeforeNow ? "text-orange-400" : "text-slate-400"
              }`}
            >
              <MdAccessTime title={plannedText} />
              {formatDate(plannedDeliveryTime, true, true)}
            </span>
          );
        return "";
      },
      size: 150,
      enableColumnFilter: false,
    },
    {
      id: "subject",
      accessorKey: "subject",
      header: "Temat",
      size: 300,
      enableColumnFilter: false,
    },
  ];

  const columns = React.useMemo<TanTableColumn<EmailApi>[]>(() => cols, []);
  return (
    <div>
      <EmailPageHeader>Historia wiadomości email</EmailPageHeader>
      <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
        Wyświetlane są maile wysłane w ciągu ostatnich 90 dni — starsze są automatycznie usuwane.
      </p>
      <TanTable
        data={emails}
        className="mt-4"
        columns={columns}
        total={total}
        loading={isLoading}
        fetching={isFetching}
        onPaginationChange={onPaginationChange}
        onSortingChange={onSortingChange}
        pagination={pagination}
        sorting={sorting}
        onClearFilters={() => updateAllFilters([])}
        onFiltersChange={(filters) => {
          updateAllFilters(filters.map((x) => ({ key: x.id, value: x.apiFilter })));
        }}
        initialFilters={[
          {
            id: "id",
            value: initValues.id,
          },
          {
            id: "type",
            value: initValues.type,
          },
          {
            id: "recipient",
            value: initValues.recipient,
          },
        ]}
        onRowClick={(x) => handleViewEmail(x)}
      />
      <AdminResourceDeleteModal
        title="Usuń email"
        description="Jesteś pewien że chcesz usunąć email?"
        onSuccessDelete={() => invalidate()}
        deleteAction={emailsApi.delete}
      />

      <ViewEmailDialog emailId={selectedEmailId} onCloseRequest={() => setSelectedEmailId(null)} />
    </div>
  );
};

const ViewEmailDialog = ({ emailId, onCloseRequest }: { emailId: number | null; onCloseRequest: () => void }) => {
  const { isLoading, email } = useEmail(emailId);

  return (
    <PaziaModal id="view-email-dialog" onCloseRequest={onCloseRequest} isOpen={!!emailId} size="4xl">
      <ModalPadding>
        <ModalHeader>Podgląd emaila</ModalHeader>
        {isLoading && <PaziaLoader position="center" />}
        {email && (
          <div>
            <div className="flex gap-2 rounded-lg bg-slate-100">
              <div className="flex items-center rounded-l-lg bg-slate-200 px-3 py-2">Temat</div>
              <div className="py-2 text-sm">{email.subject}</div>
            </div>
            <div
              dangerouslySetInnerHTML={{ __html: email.body }}
              style={{ padding: "20px", backgroundColor: "#f9f9f9" }}
            />
          </div>
        )}
      </ModalPadding>
    </PaziaModal>
  );
};
